// Farming vs Hunting tab — grouped by (BU, deal type) with win-rate column.
//
// Cell-level drill-through (SGNL-61): every $ column is clickable.
// Sortable columns (SGNL-63): header click toggles sort.

import { setActiveTab, setFilters } from "../state";
import type { Filters, OpportunityRow, StageState } from "../types";
import { computeFarmingBreakdown, type FarmingBreakdownRow } from "../lib/aggregations";
import { esc, fmtMoney } from "../lib/format";
import { bindSortHeaders, makeSortable, type SortColumn, type SortState } from "../lib/sortable";

const fmtPct = (v: number | null): string => (v === null ? "—" : `${(v * 100).toFixed(0)}%`);

const COLUMNS: SortColumn<FarmingBreakdownRow>[] = [
  { key: "bu", label: "BU" },
  { key: "type", label: "Deal type" },
  { key: "deals", label: "Deals", numeric: true },
  { key: "won_n", label: "Won #", numeric: true },
  { key: "lost_n", label: "Lost #", numeric: true },
  { key: "won", label: "Won $", numeric: true },
  { key: "lost", label: "Lost $", numeric: true },
  { key: "open", label: "Open $", numeric: true },
  { key: "win_rate", label: "Win rate", numeric: true },
];

let sortState: SortState<FarmingBreakdownRow> = { field: "won", dir: "desc" };

function drillCell(
  value: number,
  bu: string | null,
  type: string | null,
  state?: StageState,
  stage?: string,
): string {
  const buAttr = bu ? `data-bu="${esc(bu)}"` : "";
  const typeAttr = type ? `data-type="${esc(type)}"` : "";
  const stateAttr = state ? `data-state="${esc(state)}"` : "";
  const stageAttr = stage ? `data-stage="${esc(stage)}"` : "";
  return `<a href="#deals" class="drill-cell" ${buAttr} ${typeAttr} ${stateAttr} ${stageAttr}>${fmtMoney(value)}</a>`;
}

export function renderFarmingTab(host: HTMLElement, rows: OpportunityRow[]): void {
  const agg = computeFarmingBreakdown(rows);
  const { sorted, headHtml } = makeSortable(agg, COLUMNS, sortState);

  host.innerHTML = `
    <div class="tab-content" data-testid="farming-tab">
      <div class="tab-header">
        <h2>Farming vs Hunting <span class="row-count">${agg.length} (BU × type) groups</span></h2>
      </div>
      <div class="table-wrap">
        <table class="data-table sortable">
          ${headHtml}
          <tbody>
            ${sorted
              .map(
                (a) => `
              <tr>
                <td><a href="#deals" class="drill-cell" data-bu="${esc(a.bu)}" data-type="${esc(a.type)}">${esc(a.bu) || "(unset)"}</a></td>
                <td>${esc(a.type) || "(unset)"}</td>
                <td class="num">${a.deals.toLocaleString()}</td>
                <td class="num">${a.won_n.toLocaleString()}</td>
                <td class="num">${a.lost_n.toLocaleString()}</td>
                <td class="num">${drillCell(a.won, a.bu, a.type, "won")}</td>
                <td class="num">${drillCell(a.lost, a.bu, a.type, "lost", "Closed Lost")}</td>
                <td class="num">${drillCell(a.open, a.bu, a.type, "open")}</td>
                <td class="num">${fmtPct(a.win_rate)}</td>
              </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;

  for (const a of host.querySelectorAll<HTMLAnchorElement>("a.drill-cell")) {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const patch: Partial<Filters> = {};
      if (a.dataset.bu) patch.bu = a.dataset.bu;
      if (a.dataset.type) patch.type = a.dataset.type;
      if (a.dataset.state) patch.state = a.dataset.state as StageState;
      // SGNL-249: drill-through carries one stage; set the multi-
      // select list to exactly that stage (replacing any prior).
      if (a.dataset.stage) patch.stages = [a.dataset.stage];
      setFilters(patch);
      setActiveTab("deals");
    });
  }

  bindSortHeaders(host, COLUMNS, sortState, (next) => {
    sortState = next;
    renderFarmingTab(host, rows);
  });
}
